import { apiClient, logger, mapResponseToReceipt } from '@kuow/core';
import type { DonationResponse, DonationForm, DoubleTheDonationCompany, SubmissionError, DonationSubmissionResult } from '@kuow/core';
import { hasPaypalApproval } from '../paypalCheckout';
import type { DonationFormState, FormData } from '../types';

const DONATION_UPDATE_TIMEOUT_MS = 60_000;

function uncertainPaypalResult(): DonationSubmissionResult {
  return { success: false, errors: [{ code: 'PAYPAL_COMPLETION_UNCERTAIN',
    message: 'We could not confirm the result of your PayPal donation. Please check your email or contact membership@kuow.org before trying again.' }] };
}


function paypalCompletionError(error: unknown, formData: FormData): DonationSubmissionResult | null {
  const status = error instanceof Error && 'status' in error ? error.status : undefined;
  const code = error instanceof Error && 'code' in error ? String(error.code) : undefined;
  // Core can return an uncoded 400 after capture if saving local records
  // fails. Only explicit payment failures or pre-payment access/lookup
  // failures are safe to retry; an HTTP 4xx alone is not proof of no charge.
  const safeToRetry = (status === 400 && ['4001', '4002', '4003'].includes(code ?? ''))
    || status === 401 || status === 403 || status === 404 || status === 422;
  if (formData.paymentMethod === 'paypal' && formData.paypalApproval && !safeToRetry) {
    return uncertainPaypalResult();
  }

  return null;
}

/**
 * Maps payment method from form data to API format
 */
export function mapPaymentMethod(formData: FormData) {
  if (formData.paymentMethod === 'card' && formData.cardData) {
    return {
      account_type: 'credit_card',
      credit_card: {
        number: formData.cardData.cardNumber.replace(/ /g, ''),
        exp_month: formData.cardData.expMonth,
        exp_year: formData.cardData.expYear,
        bin: formData.cardData.bin,
        data_value: formData.cardData.dataValue,
        data_descriptor: formData.cardData.dataDescriptor,
      },
    };
  }

  if (formData.paymentMethod === 'bank' && formData.bankData) {
    return {
      account_type: 'bank_account',
      bank_account: {
        first_name: formData.bankData.firstName,
        last_name: formData.bankData.lastName,
        bank_name: formData.bankData.bankName,
        account_type: formData.bankData.accountType,
        routing_number: formData.bankData.routingNumber,
        account_number: formData.bankData.accountNumber,
      },
    };
  }

  if (formData.paymentMethod === 'bill-later') {
    // A promise: no payment is taken and no invoice is delivered. Fulfillment
    // happens in Salesforce.
    return {
      account_type: 'bill_later',
      delivery_method: 'none',
    };
  }

  if (formData.paymentMethod === 'paypal') {
    return {
      account_type: 'paypal',
      ...(formData.paypalApproval ? { paypal: {
        ...(formData.paypalApproval.kind === 'order'
          ? { order_id: formData.paypalApproval.id } : { subscription_id: formData.paypalApproval.id }),
        ...(formData.paypalApproval.fundingSource ? { funding_source: formData.paypalApproval.fundingSource } : {}),
      } } : {}),
    };
  }

  if (formData.paymentMethod === 'apple-pay' && formData.applePayData?.token) {
    return {
      account_type: 'apple_pay',
      apple_pay: {
        payment_data: formData.applePayData.token,
      },
    };
  }

  throw new Error('Invalid payment method');
}

/**
 * Maps member information from form data to API format
 */
function mapMemberInfo(formData: FormData, hasGift: boolean, alwaysShowAddress = false) {
  const member = {
    email: formData.email,
    first_name: formData.firstName,
    last_name: formData.lastName,
    phone_number: formData.phone.replace(/\D/g, ''), // Remove all non-numeric characters
    newsletter: formData.newsletterOptIn,
  };

  // Include shipping address if gift is selected or always_show_address is enabled, and fields are filled
  if ((hasGift || alwaysShowAddress) && formData.street && formData.city && formData.state && formData.country && formData.postalCode) {
    return {
      ...member,
      shipping_address: {
        country: formData.country,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
      },
    };
  }

  return member;
}

/**
 * Maps gift variants from form data to API format
 */
function mapGiftVariants(formData: FormData) {
  if (!formData.gift || formData.noGift) {
    return [];
  }

  return [
    {
      gift_variant_id: formData.gift.variantId ?? formData.gift.giftId,
      quantity: 1,
    },
  ];
}

export interface SubmitDonationOptions {
  memberId?: number | null;
  withAuth?: boolean;
  sfmcSubkey?: string | null;
}

/**
 * Submits a donation to the API
 */
export async function submitDonation(
  formData: FormData,
  formDetails: DonationForm,
  utmCampaign: string | null,
  options?: SubmitDonationOptions
): Promise<DonationSubmissionResult> {
  try {
    if (formData.paypalCompletionUncertain) return uncertainPaypalResult();
    if (formData.paymentMethod === 'paypal' && !hasPaypalApproval(formData, formDetails)) {
      return { success: false, errors: [{ code: '4003', message: 'Please approve your donation with PayPal again.' }] };
    }
    const hasGift = formData.gift !== null && !formData.noGift;
    const alwaysShowAddress = Boolean(formDetails.always_show_address);
    const baseAmount = formData.amount ?? formData.customAmount ?? 0;
    const totalAmount = formData.totalDonationAmount ?? baseAmount;

    // Build request payload
    const payload: Record<string, unknown> = {
      amount: totalAmount,
      cover_cost_amount: formData.coverTheCost ? formData.coverTheCostAmount : 0,
      monthly: formData.frequency === 'monthly',
      email_opt_in: formData.newsletterOptIn,
      campaign_id: formDetails.campaign.id,
      form_id: formDetails.id,
      bill_date: new Date().toISOString(),
      member: mapMemberInfo(formData, hasGift, alwaysShowAddress),
      payment_method: mapPaymentMethod(formData),
      donation_gift_variants: mapGiftVariants(formData),
      captcha_response: formData.captcha,
      utm_campaign_param: utmCampaign
    };

    if (options?.sfmcSubkey) {
      payload.sfmc_subkey = options.sfmcSubkey;
    }

    // For admin-created donations, include member_id to associate with existing member
    if (options?.memberId) {
      payload.member_id = options.memberId;
    }

    logger.info('Submitting donation', {
      amount: totalAmount,
      monthly: payload.monthly,
      campaignId: formDetails.campaign.id,
      hasGift,
      memberId: options?.memberId,
    });

    // Submit to API
    const response = await apiClient.post<DonationResponse>(
      '/donate/v1/donations',
      payload,
      { timeout: DONATION_UPDATE_TIMEOUT_MS, withAuth: options?.withAuth }
    );

    // Check for completion URL
    if (!response.completion_url && !formData.paypalApproval) {
      logger.warn('Donation created but no completion URL provided', {
        donationId: response.id,
      });
      return {
        success: false,
        errors: [
          {
            message: 'There was a problem loading your receipt. Please check your email.',
            code: 'NO_COMPLETION_URL',
          },
        ],
      };
    }

    logger.info('Donation submitted successfully', {
      donationId: response.id,
      receiptId: response.receipt_id,
    });

    return {
      success: true,
      receipt: mapResponseToReceipt(response),
      redirectUrl: formData.paypalApproval ? undefined : response.completion_url,
      errors: [],
    };
  } catch (error) {
    logger.error('Failed to submit donation', { error });
    const uncertain = paypalCompletionError(error, formData);
    if (uncertain) return uncertain;

    // Parse error response
    const errors: SubmissionError[] = [];
    
    if (error && typeof error === 'object' && 'message' in error) {
      const errorCode =
        'code' in error && (typeof error.code === 'string' || typeof error.code === 'number')
          ? String(error.code)
          : 'UNKNOWN_ERROR';
      errors.push({
        message: error.message as string,
        code: errorCode,
      });
    } else {
      errors.push({
        message: 'Sorry, there was a problem processing your request. Please contact us for assistance at membership@kuow.org or call 206-221-2501.',
        code: 'UNKNOWN_ERROR',
      });
    }

    return {
      success: false,
      errors,
    };
  }
}

export async function submitDonationUpgrade(
  formData: FormData,
  formDetails: DonationForm,
  subscription: DonationResponse,
  jwt: string,
  useNewPaymentMethod: boolean,
  utmCampaign: string | null
): Promise<DonationSubmissionResult> {
  if (formData.paypalCompletionUncertain) return uncertainPaypalResult();
  const isPaypal = formData.paymentMethod === 'paypal';
  if (isPaypal && (!useNewPaymentMethod || !jwt || formData.frequency !== 'monthly'
    || formData.upgradeDonationId !== subscription.id || !hasPaypalApproval(formData, formDetails))) {
    return { success: false, errors: [{ code: '4003', message: 'Please approve your updated donation with PayPal.' }] };
  }
  if (!useNewPaymentMethod && subscription.payment_method?.account_type === 'paypal') {
    return { success: false, errors: [{ code: '4003', message: 'Please approve your updated donation with PayPal.' }] };
  }
  try {
    const hasGift = formData.gift !== null && !formData.noGift;
    const alwaysShowAddress = Boolean(formDetails.always_show_address);
    const baseAmount = formData.amount ?? formData.customAmount ?? 0;
    const totalAmount = formData.totalDonationAmount ?? baseAmount;
    const previousAmount = typeof subscription.amount === 'string' ? parseFloat(subscription.amount) : subscription.amount;

    const payload: Record<string, unknown> = {
      jwt,
      amount: totalAmount,
      cover_cost_amount: formData.coverTheCost ? formData.coverTheCostAmount : 0,
      campaign_code: formDetails.campaign.code,
      form_id: formDetails.id,
      previous_amount: previousAmount,
      email_opt_in: formData.newsletterOptIn,
      member: mapMemberInfo(formData, hasGift, alwaysShowAddress),
      donation_gift_variants: mapGiftVariants(formData),
      utm_campaign_param: utmCampaign
    };

    if (useNewPaymentMethod) {
      payload.payment_method = mapPaymentMethod(formData);
    }

    logger.info('Submitting donation upgrade', {
      subscriptionId: subscription.id,
      amount: totalAmount,
      previousAmount,
      hasGift,
      useNewPaymentMethod,
    });

    const response = await apiClient.put<DonationResponse>(
      `/donate/v1/donations/update_subscription/${subscription.id}`,
      payload,
      { timeout: DONATION_UPDATE_TIMEOUT_MS }
    );

    if (!isPaypal && !response.completion_url) {
      logger.warn('Donation upgrade created but no completion URL provided', {
        donationId: response.id,
      });
      return {
        success: false,
        errors: [
          {
            message: 'There was a problem loading your receipt. Please check your email.',
            code: 'NO_COMPLETION_URL',
          },
        ],
      };
    }

    logger.info('Donation upgrade submitted successfully', {
      donationId: response.id,
      receiptId: response.receipt_id,
    });

    return {
      success: true,
      receipt: mapResponseToReceipt(response),
      redirectUrl: isPaypal ? undefined : response.completion_url,
      errors: [],
    };
  } catch (error) {
    logger.error('Failed to submit donation upgrade', { error });

    const uncertain = paypalCompletionError(error, formData);
    if (uncertain) return uncertain;
    const errors: SubmissionError[] = [];
    if (error && typeof error === 'object' && 'message' in error) {
      const errorCode =
        'code' in error && (typeof error.code === 'string' || typeof error.code === 'number')
          ? String(error.code)
          : 'UNKNOWN_ERROR';
      errors.push({
        message: error.message as string,
        code: errorCode,
      });
    } else {
      errors.push({
        message:
          'Sorry, there was a problem processing your request. Please contact us for assistance at membership@kuow.org or call 206-221-2501.',
        code: 'UNKNOWN_ERROR',
      });
    }

    return {
      success: false,
      errors,
    };
  }
}

export function submitBillingUpdate(
  formData: FormData,
  existingDonation: DonationResponse,
  updateToken: string,
  utmCampaign: string | null
): Promise<DonationSubmissionResult> {
  return completeBillingUpdate(formData, existingDonation, utmCampaign, { updateToken });
}

export function submitBillingUpdateWithAuth(
  formData: FormData,
  existingDonation: DonationResponse,
  utmCampaign: string | null
): Promise<DonationSubmissionResult> {
  return completeBillingUpdate(formData, existingDonation, utmCampaign, { withAuth: true });
}

export function submitBillingUpdateFromState(state: DonationFormState, formData = state.formData): Promise<DonationSubmissionResult> {
  const donation = state.activeSubscription;
  if (!donation || !('receipt_id' in donation) || (!state.billingUpdateWithAuth && !state.billingUpdateToken)) {
    return Promise.resolve({ success: false, errors: [{ message: 'Missing billing update details or credentials.' }] });
  }
  return completeBillingUpdate(formData, donation, state.utmCampaign, {
    withAuth: state.billingUpdateWithAuth,
    updateToken: state.billingUpdateWithAuth ? undefined : state.billingUpdateToken ?? undefined,
  });
}

async function completeBillingUpdate(
  formData: FormData,
  donation: DonationResponse,
  utmCampaign: string | null,
  options: { updateToken?: string; withAuth?: boolean }
): Promise<DonationSubmissionResult> {
  if (formData.paypalCompletionUncertain) return uncertainPaypalResult();
  const isPaypal = formData.paymentMethod === 'paypal';
  const billDate = donation.next_bill_date ?? donation.bill_date ?? formData.billDate ?? new Date().toISOString();
  if (isPaypal && (!donation.form || formData.frequency !== 'monthly'
    || formData.billingDonationId !== donation.id || formData.billDate !== billDate
    || !hasPaypalApproval(formData, donation.form))) {
    return { success: false, errors: [{ code: '4003', message: 'Please approve your billing update with PayPal again.' }] };
  }
  try {
    const payload = {
      ...(options.updateToken ? { update_token: options.updateToken } : {}),
      amount: formData.totalDonationAmount ?? formData.amount ?? formData.customAmount ?? 0,
      cover_cost_amount: formData.coverTheCost ? formData.coverTheCostAmount : 0,
      bill_date: billDate,
      payment_method: mapPaymentMethod(formData),
      utm_campaign_param: utmCampaign,
    };
    const query = options.updateToken ? `?update_token=${encodeURIComponent(options.updateToken)}` : '';
    const response = await apiClient.put<DonationResponse>(
      `/donate/v1/donations/${donation.id}${query}`, payload,
      { timeout: DONATION_UPDATE_TIMEOUT_MS, withAuth: Boolean(options.withAuth) }
    );
    if (!response.completion_url && !isPaypal) {
      return { success: false, errors: [{ code: 'NO_COMPLETION_URL', message: 'There was a problem loading your receipt. Please check your email.' }] };
    }
    return { success: true, receipt: mapResponseToReceipt(response),
      redirectUrl: isPaypal ? undefined : response.completion_url, errors: [] };
  } catch (error) {
    logger.error('Failed to submit billing update', { error });
    const uncertain = paypalCompletionError(error, formData);
    if (uncertain) return uncertain;
    return { success: false, errors: [{
      message: error instanceof Error ? error.message : 'There was a problem updating your billing information. Please contact membership@kuow.org.',
      code: error instanceof Error && 'code' in error ? String(error.code) : 'UNKNOWN_ERROR',
    }] };
  }
}

/**
 * Submits a dedication comment for a donation
 */
export async function submitDonationComment(
  donationId: number,
  receiptId: string,
  comment: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const payload = {
      receipt_id: receiptId,
      dedicated_to: comment,
    };

    logger.info('Submitting donation comment', { donationId, receiptId });

    await apiClient.put(`/donate/v1/donations/details/${donationId}`, payload);

    logger.info('Donation comment submitted successfully', { donationId });

    return { success: true };
  } catch (error) {
    logger.error('Failed to submit donation comment', { donationId, error });

    return {
      success: false,
      error: error && typeof error === 'object' && 'message' in error
        ? (error.message as string)
        : 'Failed to submit comment',
    };
  }
}

/**
 * Submits Double the Donation company information
 */
export async function submitDoubleTheDonation(
  donationId: number,
  receiptId: string,
  company: DoubleTheDonationCompany
): Promise<{ success: boolean; error?: string }> {
  try {
    const payload = {
      receipt_id: receiptId,
      dtd_entered_text: company.enteredText,
      dtd_status: company.status,
      dtd_company_id: company.id,
      dtd_company_name: company.name,
    };

    logger.info('Submitting Double the Donation info', { 
      donationId, 
      receiptId,
      companyName: company.name,
    });

    await apiClient.put(`/donate/v1/donations/details/${donationId}`, payload);

    logger.info('Double the Donation info submitted successfully', { donationId });

    return { success: true };
  } catch (error) {
    logger.error('Failed to submit Double the Donation info', { donationId, error });

    return {
      success: false,
      error: error && typeof error === 'object' && 'message' in error
        ? (error.message as string)
        : 'Failed to submit company matching information',
    };
  }
}
